import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';
import { formatDate } from '../utils/dates';
import { daysUntilExpiry, expiryLabel } from '../utils/expiry';
import { calculateProfit, profitByProduct, inventoryValue, findUnitMismatches, isRealSale, isPaidSale } from '../utils/profit';
import { formatStockWithRetail } from '../utils/uom';
import { t } from '../utils/translations';
import './Dashboard.css';
import './POS.css';
import './Returns.css';
import './CustomerReport.css';

const Dashboard = () => {
  const { user, isAdmin, refreshUser } = useAuth();
  const [stats, setStats] = useState({
    products: 0,
    customers: 0,
    invoices: 0,
    totalRevenue: 0,
    totalProfit: 0,
    totalProductCost: 0
  });
  const [clientName, setClientName] = useState('');
  const [gstEnabled, setGstEnabled] = useState(Boolean(user?.gst_enabled));
  const [gstSaving, setGstSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [allProducts, setAllProducts] = useState([]);
  const [expiryFilter, setExpiryFilter] = useState('attention');
  const [expiryInStockOnly, setExpiryInStockOnly] = useState(true);
  const [profitDetail, setProfitDetail] = useState({ rows: [], invoiceDiscount: 0 });
  const [showProfitDetail, setShowProfitDetail] = useState(false);
  const [profitShowAll, setProfitShowAll] = useState(false);
  const [unitFixes, setUnitFixes] = useState([]);
  const [unitChoice, setUnitChoice] = useState({});
  const [unitFixBusy, setUnitFixBusy] = useState('');
  const [dummyLeaks, setDummyLeaks] = useState([]);
  const [dummyFixBusy, setDummyFixBusy] = useState(false);
  const [valueOpen, setValueOpen] = useState(false);
  const [valueDays, setValueDays] = useState(30);
  const [valueLoading, setValueLoading] = useState(false);
  const [valueReport, setValueReport] = useState(null);

  const loadDummyLeaks = async () => {
    try {
      const res = await axios.get('/api/inventory/dummy-stock-check');
      setDummyLeaks((res.data.leaks || []).filter((row) => Number(row.net_qty) < 0));
    } catch (error) {
      setDummyLeaks([]);
    }
  };

  const restoreDummyStock = async () => {
    if (!window.confirm('Put back the stock that dummy invoices removed? Dummy invoices should never change stock.')) return;
    setDummyFixBusy(true);
    try {
      await axios.post('/api/inventory/dummy-stock-fix');
      await Promise.all([loadDummyLeaks(), fetchStats()]);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to restore stock');
    } finally {
      setDummyFixBusy(false);
    }
  };

  const loadValueReport = async (days) => {
    setValueLoading(true);
    try {
      const res = await axios.get('/api/inventory/value-report', { params: { days } });
      setValueReport(res.data);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to load stock value report');
    } finally {
      setValueLoading(false);
    }
  };

  const REASON_LABELS = {
    sale: 'Sales',
    sale_void: 'Sale edited / deleted (stock put back)',
    adjustment: 'Manual stock changes & adjustments',
    opening: 'Opening stock',
    purchase: 'Purchases received',
    return: 'Customer returns',
    transfer_in: 'Transfers in',
    transfer_out: 'Transfers out'
  };

  const expiryRows = useMemo(() => {
    const rows = [];
    allProducts.forEach((p) => {
      const days = daysUntilExpiry(p.expiry_date);
      if (days === null) return;
      if (String(p.status || 'active') === 'inactive') return;
      if (expiryInStockOnly && !(parseFloat(p.stock_quantity) > 0)) return;
      let match = false;
      if (expiryFilter === 'expired') match = days < 0;
      else if (expiryFilter === 'attention') match = days <= 30;
      else match = days >= 0 && days <= Number(expiryFilter);
      if (match) rows.push({ product: p, days });
    });
    return rows.sort((a, b) => a.days - b.days);
  }, [allProducts, expiryFilter, expiryInStockOnly]);

  const expiryCounts = useMemo(() => {
    let expired = 0;
    let soon = 0;
    allProducts.forEach((p) => {
      const days = daysUntilExpiry(p.expiry_date);
      if (days === null) return;
      if (String(p.status || 'active') === 'inactive') return;
      if (expiryInStockOnly && !(parseFloat(p.stock_quantity) > 0)) return;
      if (days < 0) expired += 1;
      else if (days <= 30) soon += 1;
    });
    return { expired, soon };
  }, [allProducts, expiryInStockOnly]);

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchStats = async () => {
    try {
      const [productsRes, customersRes, invoicesRes] = await Promise.all([
        axios.get('/api/products'),
        axios.get('/api/customers'),
        axios.get('/api/invoices')
      ]);

      const products = productsRes.data.products || [];
      const customers = customersRes.data.customers || [];
      const invoices = invoicesRes.data.invoices || [];
      setAllProducts(products);

      const paidInvoices = invoices.filter(inv =>
        (inv.status === 'paid' || inv.status === 'completed') && !Number(inv.is_dummy)
      );
      const realInvoices = invoices.filter(inv => !Number(inv.is_dummy));
      
      const totalRevenue = paidInvoices.reduce((sum, inv) => sum + (parseFloat(inv.total_amount) || 0), 0);

      // All billed sales (what the Customer Report calls Debit) vs. only the paid ones above
      const unpaidInvoices = invoices.filter((inv) => isRealSale(inv) && !isPaidSale(inv));
      const unpaidAmount = unpaidInvoices.reduce((sum, inv) => sum + (parseFloat(inv.total_amount) || 0), 0);

      // Stock value at cost: remaining FIFO layers at their own cost (product cost for any remainder)
      const totalProductCost = inventoryValue(products);

      // Profit uses the cost saved on each invoice line at sale time (per base unit x base quantity)
      const totalProfit = isAdmin ? calculateProfit(paidInvoices, products) : 0;
      if (isAdmin) {
        loadDummyLeaks();
        setProfitDetail(profitByProduct(paidInvoices, products));
        const mismatches = findUnitMismatches(realInvoices, products);
        setUnitFixes(mismatches);
        const choice = {};
        mismatches.forEach((m) => { choice[m.key] = m.suggested.id; });
        setUnitChoice(choice);
      }

      setStats({
        products: products.length,
        customers: customers.length,
        invoices: realInvoices.length,
        totalRevenue: totalRevenue.toFixed(2),
        paidCount: paidInvoices.length,
        unpaidAmount: unpaidAmount.toFixed(2),
        unpaidCount: unpaidInvoices.length,
        totalBilled: (totalRevenue + unpaidAmount).toFixed(2),
        totalProfit: totalProfit.toFixed(2),
        totalProductCost: totalProductCost.toFixed(2)
      });

      // Fetch client name if user has a client_id
      if (user?.client_id && user?.role !== 'super_admin') {
        try {
          const clientRes = await axios.get(`/api/clients/${user.client_id}`);
          if (clientRes.data.success && clientRes.data.client) {
            setClientName(clientRes.data.client.name);
            setGstEnabled(Boolean(Number(clientRes.data.client.gst_enabled)));
          }
        } catch (error) {
          console.error('Error fetching client:', error);
          // If client fetch fails, keep empty string
        }
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleGst = async (enabled) => {
    if (!user?.client_id) return;
    setGstSaving(true);
    try {
      await axios.put(`/api/clients/${user.client_id}`, { gst_enabled: enabled });
      setGstEnabled(enabled);
      if (refreshUser) await refreshUser();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update GST setting');
    } finally {
      setGstSaving(false);
    }
  };

  const currentUomIdFor = (item, product) => {
    const uoms = (product && product.uoms) || [];
    const factor = parseFloat(item.conversion_to_base) || 1;
    const match = uoms.find((u) => u.id === item.uom_id)
      || uoms.find((u) => Math.abs((parseFloat(u.conversion_to_base) || 1) - factor) < 1e-6);
    return match ? match.id : undefined;
  };

  // Re-saves an invoice with corrected units; the server then fixes stock, cost layers and profit cost.
  const saveInvoiceUnits = async (invoice, uomByItemId) => {
    const lines = invoice.items || [];
    if (lines.some((line) => !(parseFloat(line.quantity) > 0))) {
      throw new Error(`Invoice ${invoice.invoice_number} has a fully returned line. Fix its unit from the Invoices page.`);
    }
    const productById = {};
    allProducts.forEach((p) => { productById[p.id] = p; });
    const hasGst = Number(invoice.tax_amount) > 0 || lines.some((line) => Number(line.gst_rate) > 0);
    await axios.put(`/api/invoices/${invoice.id}`, {
      apply_gst: hasGst,
      items: lines.map((line) => ({
        product_id: line.product_id,
        description: line.description,
        quantity: line.quantity,
        length: line.length,
        width: line.width,
        unit_price: line.unit_price,
        discount: line.discount,
        gst_rate: line.gst_rate,
        gst_inclusive: Boolean(Number(line.gst_inclusive)),
        uom_id: uomByItemId[line.id] || currentUomIdFor(line, productById[line.product_id])
      }))
    });
  };

  const fixUnits = async (rows) => {
    const byInvoice = new Map();
    rows.forEach((row) => {
      if (!byInvoice.has(row.invoice.id)) byInvoice.set(row.invoice.id, { invoice: row.invoice, map: {} });
      byInvoice.get(row.invoice.id).map[row.item.id] = unitChoice[row.key] || row.suggested.id;
    });
    setUnitFixBusy(rows.length === 1 ? rows[0].key : 'all');
    const failures = [];
    for (const { invoice, map } of byInvoice.values()) {
      try {
        await saveInvoiceUnits(invoice, map);
      } catch (error) {
        failures.push(error.response?.data?.message || error.message || `Failed on ${invoice.invoice_number}`);
      }
    }
    setUnitFixBusy('');
    if (failures.length) alert(failures.join('\n'));
    await fetchStats();
  };

  if (loading) {
    return <div className="page-container"><div className="container">{t('Loading...')}</div></div>;
  }

  return (
    <div className="page-container">
      <div className="container">
        <div className="page-header">
          <h1>{t('Dashboard')}</h1>
          <div>
            <strong>{t('Client')}:</strong> {
              user?.role === 'super_admin' 
                ? t('Super Admin')
                : clientName || (user?.client_id ? t('Client') + ' #' + user.client_id.substring(0, 8) : t('N/A'))
            }
          </div>
        </div>

        <div className="dashboard-quick-actions">
          <Link to="/pos" className="btn dashboard-action-btn dashboard-action-pos">
            Open POS
          </Link>
          <Link to="/returns" className="btn dashboard-action-btn dashboard-action-returns">
            Manage Returns
          </Link>
          <Link to="/reports" className="btn dashboard-action-btn dashboard-action-report">
            Reports
          </Link>
          {isAdmin && (
            <Link to="/purchases" className="btn dashboard-action-btn dashboard-action-purchases">
              Purchases
            </Link>
          )}
        </div>

        {user?.client_id && user?.role !== 'super_admin' && (
          <div className="card" style={{ marginBottom: '20px' }}>
            <h3 style={{ marginTop: 0 }}>GST Settings</h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                checked={gstEnabled}
                disabled={gstSaving}
                onChange={(e) => toggleGst(e.target.checked)}
              />
              Enable GST for this business
            </label>
            <p style={{ margin: '8px 0 0', color: '#666', fontSize: '14px' }}>
              When enabled, you can set GST % on products (include or exclude GST) and invoices will calculate GST automatically.
            </p>
          </div>
        )}

        <div className="stats-grid">
          <div className="stat-card primary">
            <h3>{t('Total Products')}</h3>
            <div className="value">{stats.products}</div>
          </div>
          <div className="stat-card success">
            <h3>{t('Total Customers')}</h3>
            <div className="value">{stats.customers}</div>
          </div>
          <div className="stat-card warning">
            <h3>{t('Total Invoices')}</h3>
            <div className="value">{stats.invoices}</div>
          </div>
          <div className="stat-card danger">
            <h3>{t('Total Revenue')}</h3>
            <div className="value">{formatCurrency(stats.totalRevenue)}</div>
            <div style={{ fontSize: 12, opacity: 0.9, marginTop: 4, lineHeight: 1.4 }}>
              Paid invoices only ({stats.paidCount || 0}).
              {Number(stats.unpaidCount) > 0 && (
                <>
                  <br />
                  Unpaid / partly paid: {formatCurrency(stats.unpaidAmount)} ({stats.unpaidCount})
                  <br />
                  Total billed: {formatCurrency(stats.totalBilled)}
                </>
              )}
            </div>
          </div>
          <div className="stat-card" style={{ background: 'linear-gradient(135deg, #0f766e 0%, #115e59 100%)', color: '#fff' }}>
            <h3 style={{ color: '#fff' }}>Total Product Cost</h3>
            <div className="value" style={{ color: '#fff' }}>{formatCurrency(stats.totalProductCost)}</div>
            <div style={{ fontSize: 12, opacity: 0.9, marginTop: 4 }}>Cost × stock (all products)</div>
          </div>
          {/* Profit card - Only for Admin users */}
          {isAdmin && (
            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}>
              <h3 style={{ color: '#fff' }}>{t('Total Profit')}</h3>
              <div className="value" style={{ color: '#fff' }}>{formatCurrency(stats.totalProfit)}</div>
            </div>
          )}
        </div>

        {isAdmin && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <h3 style={{ margin: 0 }}>Profit breakdown by product</h3>
                <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>
                  Profit = sales (excl. GST) − product cost × quantity sold − invoice discounts
                  ({formatCurrency(profitDetail.invoiceDiscount)} discounts).
                  Losing products are listed first.
                </div>
              </div>
              <button type="button" className="btn btn-secondary" onClick={() => setShowProfitDetail((v) => !v)}>
                {showProfitDetail ? 'Hide' : 'Show'} details
              </button>
            </div>
            {showProfitDetail && (
              <>
                <label className="dashboard-expiry-check" style={{ marginTop: 12, display: 'inline-flex' }}>
                  <input
                    type="checkbox"
                    checked={profitShowAll}
                    onChange={(e) => setProfitShowAll(e.target.checked)}
                  />
                  Show all products (default: 25 worst)
                </label>
                <div className="dashboard-expiry-table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Qty sold (base)</th>
                        <th>Sales</th>
                        <th>Cost / base unit (at sale)</th>
                        <th>Price / base unit</th>
                        <th>Cost</th>
                        <th>Profit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(profitShowAll ? profitDetail.rows : profitDetail.rows.slice(0, 25)).map((row) => (
                        <tr key={row.key} style={row.profit < 0 ? { background: '#fef2f2' } : undefined}>
                          <td>
                            {row.name}
                            {row.missingProduct && <span style={{ color: '#b45309' }}> (product removed)</span>}
                            {row.costAbovePrice && <span style={{ color: '#b91c1c' }}> ⚠ cost &gt; price</span>}
                            {row.estimated && <span style={{ color: '#6b7280' }} title="Older sale without a saved cost; today's product cost is used"> (est.)</span>}
                          </td>
                          <td>{Math.round(row.baseQty * 1000) / 1000} {row.unit}</td>
                          <td>{formatCurrency(row.revenue)}</td>
                          <td>{formatCurrency(row.unitCost)}</td>
                          <td>{formatCurrency(row.unitPrice)}</td>
                          <td>{formatCurrency(row.cost)}</td>
                          <td style={{ color: row.profit < 0 ? '#b91c1c' : '#047857', fontWeight: 600 }}>
                            {formatCurrency(row.profit)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {isAdmin && dummyLeaks.length > 0 && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <h3 style={{ margin: 0 }}>Dummy invoices removed stock ({dummyLeaks.length})</h3>
                <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>
                  Dummy invoices must never change stock. These did. Restoring puts the stock back.
                </div>
              </div>
              <button type="button" className="btn btn-primary" disabled={dummyFixBusy} onClick={restoreDummyStock}>
                {dummyFixBusy ? 'Restoring…' : 'Restore stock'}
              </button>
            </div>
            <div className="dashboard-expiry-table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Invoice</th><th>Product</th><th>Stock taken</th><th>Value</th></tr>
                </thead>
                <tbody>
                  {dummyLeaks.map((row) => (
                    <tr key={`${row.invoice_id}:${row.product_id}`}>
                      <td>{row.invoice_number}</td>
                      <td>{row.product_name || 'Deleted product'}</td>
                      <td>{Math.round(Math.abs(Number(row.net_qty)) * 1000) / 1000} {row.unit}</td>
                      <td>{formatCurrency(Math.abs(Number(row.net_qty)) * Number(row.cost))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {isAdmin && unitFixes.length > 0 && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <h3 style={{ margin: 0 }}>Possible unit mistakes ({unitFixes.length})</h3>
                <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>
                  These lines were billed at the price of a smaller unit (e.g. packet) but saved in another unit
                  (e.g. box). That removes too much stock and charges too much cost. Check the suggested unit
                  and fix: stock, cost and profit are corrected automatically.
                </div>
              </div>
              <button
                type="button"
                className="btn btn-primary"
                disabled={Boolean(unitFixBusy)}
                onClick={() => {
                  if (window.confirm(`Re-save ${unitFixes.length} line(s) with the suggested units? Stock will be corrected.`)) {
                    fixUnits(unitFixes);
                  }
                }}
              >
                {unitFixBusy === 'all' ? 'Fixing…' : `Fix all (${unitFixes.length})`}
              </button>
            </div>
            <div className="dashboard-expiry-table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Product</th>
                    <th>Billed</th>
                    <th>Saved as</th>
                    <th>Should be</th>
                    <th>Stock put back</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {unitFixes.map((row) => {
                    const chosen = (row.product.uoms || []).find((u) => u.id === (unitChoice[row.key] || row.suggested.id)) || row.suggested;
                    const qty = parseFloat(row.item.quantity) || 0;
                    const oldBase = qty * (parseFloat(row.item.conversion_to_base) || 1);
                    const newBase = qty * (parseFloat(chosen.conversion_to_base) || 1);
                    const unitCost = row.item.cost_price_base != null && row.item.cost_price_base !== ''
                      ? parseFloat(row.item.cost_price_base)
                      : parseFloat(row.product.cost) || 0;
                    const putBack = oldBase - newBase;
                    return (
                      <tr key={row.key}>
                        <td>
                          {row.invoice.invoice_number}
                          <div style={{ color: '#6b7280', fontSize: 12 }}>{formatDate(row.invoice.issue_date)}</div>
                        </td>
                        <td>{row.product.name}</td>
                        <td>{qty} × {formatCurrency(row.item.unit_price)}</td>
                        <td>{row.currentCode}</td>
                        <td>
                          <select
                            value={unitChoice[row.key] || row.suggested.id}
                            onChange={(e) => setUnitChoice((prev) => ({ ...prev, [row.key]: e.target.value }))}
                          >
                            {(row.product.uoms || []).map((u) => (
                              <option key={u.id} value={u.id}>{u.code}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          {Math.round(putBack * 1000) / 1000} {row.product.unit}
                          <div style={{ color: '#6b7280', fontSize: 12 }}>{formatCurrency(putBack * unitCost)}</div>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            disabled={Boolean(unitFixBusy)}
                            onClick={() => fixUnits([row])}
                          >
                            {unitFixBusy === row.key ? 'Fixing…' : 'Fix'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <h3 style={{ margin: 0 }}>Where did stock value go?</h3>
                <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>
                  Every stock change in the period, valued at current cost. Negative = stock value went down.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select
                  value={valueDays}
                  onChange={(e) => {
                    const days = Number(e.target.value);
                    setValueDays(days);
                    if (valueOpen) loadValueReport(days);
                  }}
                >
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                  <option value={365}>Last 365 days</option>
                </select>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    const next = !valueOpen;
                    setValueOpen(next);
                    if (next && !valueReport) loadValueReport(valueDays);
                  }}
                >
                  {valueOpen ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            {valueOpen && (valueLoading ? (
              <p style={{ color: '#6b7280' }}>Loading…</p>
            ) : valueReport && (
              <>
                <div className="dashboard-expiry-table-wrap">
                  <table className="table">
                    <thead>
                      <tr><th>Reason</th><th>Entries</th><th>Stock value change</th></tr>
                    </thead>
                    <tbody>
                      {(valueReport.byReason || []).map((row) => (
                        <tr key={`${row.movement_type}:${row.reference_type}`}>
                          <td>
                            {REASON_LABELS[row.movement_type] || row.movement_type}
                            {row.reference_type && (
                              <span style={{ color: '#6b7280', fontSize: 12 }}> ({row.reference_type})</span>
                            )}
                          </td>
                          <td>{row.entries}</td>
                          <td style={{ color: Number(row.value) < 0 ? '#b91c1c' : '#047857', fontWeight: 600 }}>
                            {formatCurrency(row.value)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {(() => {
                  const soldAtCost = Math.abs(
                    (valueReport.byReason || [])
                      .filter((r) => r.movement_type === 'sale' && r.reference_type === 'invoice')
                      .reduce((sum, r) => sum + (Number(r.value) || 0), 0)
                  );
                  if (!soldAtCost) return null;
                  const billed = Number(valueReport.salesBilled) || 0;
                  const suspicious = soldAtCost > billed;
                  return (
                    <div
                      style={{
                        marginTop: 12,
                        padding: '10px 12px',
                        borderRadius: 6,
                        fontSize: 13,
                        background: suspicious ? '#fef2f2' : '#f0fdf4',
                        color: suspicious ? '#991b1b' : '#166534'
                      }}
                    >
                      In this period, invoices were <strong>billed {formatCurrency(billed)}</strong> ({valueReport.salesInvoiceCount} invoices)
                      and sales removed stock worth <strong>{formatCurrency(soldAtCost)}</strong> at cost.
                      {suspicious
                        ? ' Stock removed at cost is higher than what was billed, which cannot be right for normal sales. Usually items were saved in the wrong unit (e.g. packets saved as boxes). Check "Possible unit mistakes" above.'
                        : ' Stock removed at cost is below what was billed, as expected.'}
                    </div>
                  );
                })()}
                <h4 style={{ margin: '16px 0 4px' }}>Biggest reductions by product</h4>
                <div className="dashboard-expiry-table-wrap">
                  <table className="table">
                    <thead>
                      <tr><th>Product</th><th>Reason</th><th>Qty</th><th>Value</th></tr>
                    </thead>
                    <tbody>
                      {(valueReport.topProducts || []).filter((row) => Number(row.value) < 0).slice(0, 20).map((row) => (
                        <tr key={`${row.product_id}:${row.movement_type}`}>
                          <td>{row.product_name || 'Deleted product'}</td>
                          <td>{REASON_LABELS[row.movement_type] || row.movement_type}</td>
                          <td>{Math.round(Number(row.qty_base) * 1000) / 1000} {row.unit}</td>
                          <td style={{ color: '#b91c1c', fontWeight: 600 }}>{formatCurrency(row.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ))}
          </div>
        )}

        <div className="card dashboard-expiry-card">
          <div className="dashboard-expiry-head">
            <div>
              <h3 style={{ margin: 0 }}>Product Expiry</h3>
              <div className="dashboard-expiry-summary">
                <span className="expiry-chip expiry-chip-expired">{expiryCounts.expired} expired</span>
                <span className="expiry-chip expiry-chip-soon">{expiryCounts.soon} expiring within 30 days</span>
              </div>
            </div>
            <div className="dashboard-expiry-controls">
              <select value={expiryFilter} onChange={(e) => setExpiryFilter(e.target.value)}>
                <option value="attention">Expired + expiring in 30 days</option>
                <option value="expired">Already expired</option>
                <option value="7">Expiring in 7 days</option>
                <option value="15">Expiring in 15 days</option>
                <option value="30">Expiring in 30 days</option>
                <option value="60">Expiring in 60 days</option>
                <option value="90">Expiring in 90 days</option>
              </select>
              <label className="dashboard-expiry-check">
                <input
                  type="checkbox"
                  checked={expiryInStockOnly}
                  onChange={(e) => setExpiryInStockOnly(e.target.checked)}
                />
                In stock only
              </label>
            </div>
          </div>

          {expiryRows.length === 0 ? (
            <p style={{ color: '#6b7280', margin: '12px 0 0' }}>
              No products match this filter. Add an expiry date on a product to track it here.
            </p>
          ) : (
            <div className="dashboard-expiry-table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Expiry date</th>
                    <th>Status</th>
                    <th>Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {expiryRows.map(({ product, days }) => (
                    <tr key={product.id}>
                      <td>{product.name}</td>
                      <td>{formatDate(product.expiry_date)}</td>
                      <td>
                        <span className={`expiry-chip ${days < 0 ? 'expiry-chip-expired' : 'expiry-chip-soon'}`}>
                          {expiryLabel(days)}
                        </span>
                      </td>
                      <td>{formatStockWithRetail(product)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card">
          <h2>{t('Welcome')}, {user?.full_name}!</h2>
          <p>{t('You are logged in as')} <strong>{user?.role}</strong>.</p>
          <p>{t('Use the navigation menu to manage products, customers, and invoices.')}</p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
